import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Package, ArrowLeft, Download } from 'lucide-react';
import { toast } from 'sonner';

const OrderDetail = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${id}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Order not found');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'badge-gray',
      processing: 'badge-blue',
      completed: 'badge-green'
    };
    return styles[status] || 'badge-gray';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-xl text-gray-600">Loading order...</div>
        </div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/orders')}
          className="mb-6"
          data-testid="back-to-orders-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Orders
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Order Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 pb-8 border-b-2 border-gray-200">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="order-detail-id">Order #{order.id}</h1>
              <p className="text-gray-600">
                Placed on {new Date(order.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div className="flex flex-col gap-2 mt-4 sm:mt-0">
              <span className={`badge ${getStatusBadge(order.status)} text-center`} data-testid="order-detail-status">
                Status: {order.status}
              </span>
              <span className={`badge ${getStatusBadge(order.payment_status)} text-center`}>
                Payment: {order.payment_status}
              </span>
            </div>
          </div>

          {/* Order Items */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Items</h2>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex gap-4 p-4 bg-gray-50 rounded-lg" data-testid={`order-item-${index}`}>
                  <div className="bg-gradient-to-br from-blue-50 to-sky-100 rounded-lg w-16 h-16 flex items-center justify-center flex-shrink-0">
                    <Package className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{item.product_name}</h3>
                    <p className="text-sm text-gray-600">Quantity: {item.quantity} case(s)</p>
                    <p className="text-sm text-gray-600">${item.price_per_case.toFixed(2)} per case</p>
                    {item.discount_percent > 0 && (
                      <p className="text-sm text-green-600 font-semibold">
                        Discount applied: {item.discount_percent}% off
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900 text-lg">${item.total.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-700">Subtotal</span>
                <span className="font-semibold text-gray-900" data-testid="order-subtotal">${order.subtotal.toFixed(2)}</span>
              </div>
              {order.discount_total > 0 && (
                <div className="flex justify-between text-green-600">
                  <span className="font-semibold">Bulk Discount</span>
                  <span className="font-bold" data-testid="order-discount">-${order.discount_total.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t-2 border-blue-300 pt-3 flex justify-between items-center">
                <span className="text-xl font-bold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-blue-600" data-testid="order-detail-total">${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Customer Information</h2>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-gray-700"><strong>Company:</strong> {user.company_name}</p>
              <p className="text-gray-700"><strong>Email:</strong> {user.email}</p>
              {user.phone && <p className="text-gray-700"><strong>Phone:</strong> {user.phone}</p>}
              {user.business_type && <p className="text-gray-700"><strong>Business Type:</strong> {user.business_type}</p>}
            </div>
          </div>

          {/* Shipping Address */}
          {order.shipping_address && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">📦 Shipping Address</h2>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="text-gray-800">
                  <p className="font-semibold">{order.shipping_address.full_name}</p>
                  <p>{order.shipping_address.company}</p>
                  <p>{order.shipping_address.address_line1}</p>
                  {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                  <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
                  <p className="mt-2"><strong>Phone:</strong> {order.shipping_address.phone}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              data-testid="download-invoice-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              Download Invoice (PDF)
            </Button>
            <Button 
              onClick={() => navigate('/products')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="reorder-btn"
            >
              Reorder Products
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderDetail;